import React, { useCallback, useEffect, useState } from 'react'
import { Attachement } from '../models/shared/Attachment'
import { AdvancedButton } from './AdvancedButton'
import { useText } from '../context/TextContext'

interface Props {
    attachments: Attachement[]
    initialIndex: number
    isOpen: boolean
    onClose: () => void
}

export default function AttachmentPreview(props: Props) {
    const { attachments, initialIndex, isOpen, onClose } = props
    const [currentIndex, setCurrentIndex] = useState(initialIndex)
    const text = useText()

    // Update current index when initialIndex changes
    useEffect(() => {
        setCurrentIndex(initialIndex)
    }, [initialIndex])

    // Keyboard navigation
    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (!isOpen) return

        switch (event.key) {
            case 'Escape':
                onClose()
                break
            case 'ArrowLeft':
                event.preventDefault()
                setCurrentIndex(prev => prev > 0 ? prev - 1 : attachments.length - 1)
                break
            case 'ArrowRight':
                event.preventDefault()
                setCurrentIndex(prev => prev < attachments.length - 1 ? prev + 1 : 0)
                break
        }
    }, [isOpen, onClose, attachments.length])

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [handleKeyDown])

    // Prevent body scroll when overlay is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }
        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [isOpen])

    const currentAttachment = attachments[currentIndex]
    if (!isOpen || !attachments.length || !currentAttachment) {
        return null
    }

    const isImage = currentAttachment.fileType?.startsWith('image')

    const goToPrevious = () => {
        setCurrentIndex(prev => prev > 0 ? prev - 1 : attachments.length - 1)
    }

    const goToNext = () => {
        setCurrentIndex(prev => prev < attachments.length - 1 ? prev + 1 : 0)
    }

    const handleOverlayClick = (event: React.MouseEvent) => {
        if (event.target === event.currentTarget) {
            onClose()
        }
    }

    return (
        <div className="attachment-preview-overlay d-flex align-items-center justify-content-center" onClick={handleOverlayClick}>
            <div className="position-relative w-100 h-100 d-flex flex-column" style={{ maxWidth: '100vw', maxHeight: '100vh' }}>
                {/* Header */}
                <div className="position-absolute top-0 start-0 end-0 d-flex justify-content-between align-items-center p-3 pe-4" style={{ zIndex: 10, background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.7), transparent)' }}>
                    <div className="flex-fill" style={{ minWidth: 0 }}>
                        <h5 className="mb-0 text-white text-truncate">{currentAttachment.name}</h5>
                        <small className="text-white-50">
                            {currentIndex + 1} of {attachments.length}
                        </small>
                    </div>
                    <AdvancedButton
                        variant="clear"
                        size="sm"
                        icon="bi-x-lg"
                        onClick={onClose}
                        className="text-white"
                    />
                </div>

                {/* Content */}
                <div className="flex-fill d-flex align-items-center justify-content-center p-4" style={{ paddingTop: '5rem', paddingBottom: '7rem', minHeight: 0 }}
                    onClick={onClose}
                >
                    {isImage && currentAttachment.url !== '/' ? (
                        <img
                            onClick={e => e.stopPropagation()}
                            src={currentAttachment.url}
                            alt={currentAttachment.name}
                            className="attachment-preview-image rounded shadow-lg"
                            style={{
                                maxWidth: 'calc(100vw - 2rem)',
                                maxHeight: 'calc(100vh - 12rem)',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain'
                            }}
                        />
                    ) : (
                        <div className="text-center text-white"
                            onClick={e => e.stopPropagation()}
                        >
                            <i className="bi bi-file-earmark-text display-1 text-white mb-3" />
                            <p className="text-white mb-3">{currentAttachment.name}</p>
                            <a
                                href={currentAttachment.url}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-outline-light"
                            >
                                <i className="bi bi-download me-2" />
                                {text.download}
                            </a>
                        </div>
                    )}
                </div>

                {/* Navigation */}
                {attachments.length > 1 && (
                    <>
                        <button
                            className="attachment-preview-nav position-absolute top-50 translate-middle-y btn btn-outline-light rounded-circle d-flex align-items-center justify-content-center"
                            style={{ left: '1rem', width: '50px', height: '50px', fontSize: '1.2rem', background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(4px)', border: 'none' }}
                            onClick={goToPrevious}
                            aria-label="Previous attachment"
                        >
                            <i className="bi bi-chevron-left" />
                        </button>
                        <button
                            className="attachment-preview-nav position-absolute top-50 translate-middle-y btn btn-outline-light rounded-circle d-flex align-items-center justify-content-center"
                            style={{ right: '1rem', width: '50px', height: '50px', fontSize: '1.2rem', background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(4px)', border: 'none' }}
                            onClick={goToNext}
                            aria-label="Next attachment"
                        >
                            <i className="bi bi-chevron-right" />
                        </button>
                    </>
                )}

                {/* Thumbnails */}
                {attachments.length > 1 && (
                    <div
                        className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-center gap-2 p-3 pt-4 overflow-auto"
                        style={{ background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent)', maxWidth: '100%' }}
                    >
                        {attachments.map((attachment, index) => (
                            <button
                                key={index}
                                className={`btn p-0 border-2 rounded d-flex align-items-center justify-content-center flex-shrink-0 ${index === currentIndex ? 'border-primary' : 'border-transparent'
                                    }`}
                                style={{
                                    width: '60px',
                                    height: '60px',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    transform: index === currentIndex ? 'scale(1.1)' : 'scale(1)'
                                }}
                                onClick={() => setCurrentIndex(index)}
                            >
                                {attachment.fileType?.startsWith('image') && attachment.url !== '/' ? (
                                    <img
                                        src={attachment.url}
                                        alt={attachment.name}
                                        className="w-100 h-100 rounded"
                                        style={{ objectFit: 'cover' }}
                                    />
                                ) : (
                                    <i className="bi bi-file-earmark-text text-white fs-4" />
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}